import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, Plus, Search } from 'lucide-react';
import apiClient from '../../utils/api';

const LIMIT = 15;

export const SuperAdminCompanies: React.FC = () => {
  const [companies, setCompanies] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.listCompanies(LIMIT, page * LIMIT);
      const d = res.data.data;
      setCompanies(d?.companies ?? d?.rows ?? (Array.isArray(d) ? d : []));
      setTotal(d?.total ?? d?.count ?? 0);
    } catch {} finally { setLoading(false); }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const toggleStatus = async (c: any) => {
    const next = c.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    await apiClient.setCompanyStatus(c.id, next);
    load();
  };

  const filtered = companies.filter(c =>
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Companies</h1>
        <Link to="/super-admin/companies/new" className="btn btn-primary">
          <Plus size={16} /> New Company
        </Link>
      </div>

      <div className="card">
        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input placeholder="Search companies..." value={search} onChange={e => setSearch(e.target.value)} style={{ paddingLeft: 32, width: '100%' }} />
          </div>
        </div>

        {loading ? <div className="loading-spinner">Loading...</div> : filtered.length === 0 ? (
          <div className="empty-state"><h3>No companies found</h3></div>
        ) : (
          <>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>Name</th><th>Email</th><th>Country</th><th>Currency</th><th>API</th><th>Webhooks</th><th>Status</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {filtered.map((c: any) => (
                    <tr key={c.id}>
                      <td style={{ fontWeight: 600 }}>{c.name}</td>
                      <td>{c.email || '—'}</td>
                      <td>{c.country}</td>
                      <td>{c.currency}</td>
                      <td><span className={`badge badge-${c.allowApiIntegration ? 'active' : 'inactive'}`}>{c.allowApiIntegration ? 'On' : 'Off'}</span></td>
                      <td><span className={`badge badge-${c.allowWebhooks ? 'active' : 'inactive'}`}>{c.allowWebhooks ? 'On' : 'Off'}</span></td>
                      <td><span className={`badge badge-${c.status?.toLowerCase()}`}>{c.status}</span></td>
                      <td style={{ display: 'flex', gap: 6 }}>
                        <Link to={`/super-admin/companies/${c.id}`} className="btn btn-ghost" style={{ padding: '3px 8px', fontSize: 12 }}>View</Link>
                        <Link to={`/super-admin/companies/${c.id}/edit`} className="btn btn-ghost" style={{ padding: '3px 8px', fontSize: 12 }}>Edit</Link>
                        <button className={`btn ${c.status === 'ACTIVE' ? 'btn-danger' : 'btn-success'}`} style={{ padding: '3px 8px', fontSize: 12 }} onClick={() => toggleStatus(c)}>
                          {c.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="pagination">
                <button className="btn btn-ghost" onClick={() => setPage(p => p - 1)} disabled={page === 0}>Prev</button>
                <span>Page {page + 1} of {totalPages}</span>
                <button className="btn btn-ghost" onClick={() => setPage(p => p + 1)} disabled={page + 1 >= totalPages}>Next</button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};
